import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Editor, EditorState, RichUtils, convertToRaw, convertFromRaw, AtomicBlockUtils, Modifier, CompositeDecorator } from 'draft-js';
import { FaBold, FaItalic, FaSave, FaImage, FaYoutube, FaLink, FaHeading, FaUndo, FaRedo, FaRegImage, FaTimes } from 'react-icons/fa';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ImageUploadDialog from '../../components/ImageUploadDialog';
import LinkInsertDialog from '../../components/LinkInsertDialog';
import { EditorContainer, EditorSection, TitleInput, EditorWrapper, Toolbar, ToolbarButton } from './ArticleEditor.styles';
import { selectAllArticleTypes } from './ArticleTypeSlice';
import { useIsMobile } from '../../theme';
import 'draft-js/dist/Draft.css';

// Constants
const DEFAULT_DRAFT_INTERVAL = 30000;
const CONTENT_NESTING_KEYS = ['raw', 'data', 'payload', 'body', 'content'];
const YOUTUBE_URL_REGEX = /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/;

// Utility functions
const normalizeArticleId = (article) => {
    if (!article) return null;
    return article.id || article.idArticle || article.id_article || null;
};

const parseInterval = (interval) => {
    if (typeof interval === 'number') return interval;
    if (typeof interval === 'string') {
        const trimmed = interval.trim().toLowerCase();
        const match = trimmed.match(/^(\d+)(ms|s|m)?$/);
        if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2] || 'ms';
            const multipliers = { ms: 1, s: 1000, m: 60000 };
            return value * (multipliers[unit] || 1);
        }
    }
    return DEFAULT_DRAFT_INTERVAL;
};

const createEmptySnapshot = () => ({
    title: '',
    content: convertToRaw(EditorState.createEmpty().getCurrentContent()),
});

const extractYouTubeId = (url) => {
    const match = url.match(YOUTUBE_URL_REGEX);
    return match ? match[1] : null;
};

// Content parsing utilities
const unwrapNestedContent = (articleContent) => {
    if (!articleContent || typeof articleContent !== 'object' || Array.isArray(articleContent)) {
        return articleContent;
    }

    if (articleContent.blocks) {
        return articleContent;
    }

    // Try common nesting keys
    for (const key of CONTENT_NESTING_KEYS) {
        if (articleContent[key]?.blocks) {
            return articleContent[key];
        }
    }

    // Search any value with blocks array
    for (const value of Object.values(articleContent)) {
        if (value && typeof value === 'object' && value.blocks && Array.isArray(value.blocks)) {
            return value;
        }
    }

    return articleContent;
};

const parseArticleContent = (rawContent) => {
    if (!rawContent) return null;

    try {
        let parsedContent = typeof rawContent === 'string' ? JSON.parse(rawContent) : rawContent;
        parsedContent = unwrapNestedContent(parsedContent);

        if (!parsedContent?.blocks || !Array.isArray(parsedContent.blocks)) {
            throw new Error('Invalid content structure: missing blocks array');
        }

        // Inject empty entityMap if missing
        if (!parsedContent.entityMap) {
            parsedContent.entityMap = {};
        }

        // Validate blocks structure
        const validBlocks = parsedContent.blocks.every(
            (block) => block && typeof block === 'object' && typeof block.text === 'string' && block.type !== undefined,
        );

        if (!validBlocks) {
            throw new Error('Invalid blocks structure');
        }

        return parsedContent;
    } catch (error) {
        console.error('Error parsing article content:', error);
        return null;
    }
};

// Custom hooks
const useSnackbar = () => {
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const closeSnackbar = useCallback((_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    return { snackbar, showSnackbar, closeSnackbar };
};

const useArticleState = (initialContent, defaultTypeId) => {
    const [idArticle, setIdArticle] = useState(normalizeArticleId(initialContent));
    const [title, setTitle] = useState('');
    const [lead, setLead] = useState('');
    const [coverImage, setCoverImage] = useState(null); // { id, filename, alt } or null
    const [idArticleType, setIdArticleType] = useState(defaultTypeId || 1); // Default to the type picked in "Novo"
    // Publication state, edited here rather than only from the list.
    // POST /api/articles writes `visible` on every save (it defaults to false in
    // the controller), so an editor that never sent it silently unpublished
    // whatever it saved. Round-tripping the real value is what stops that.
    const [visible, setVisible] = useState(false);
    const decorator = useMemo(() => createDecorator(), []);
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty(decorator));
    const [dirty, setDirty] = useState(false);
    const lastSnapshotRef = useRef(createEmptySnapshot());
    const isLoadingContentRef = useRef(false); // Track if content is being loaded

    const updateSnapshot = useCallback((newTitle, newContent, id = null, articleType = null, newLead = '', newCoverId = null) => {
        lastSnapshotRef.current = {
            title: newTitle,
            content: newContent,
            lead: newLead || '',
            coverImageId: newCoverId || null,
            ...(id && { id }),
            ...(articleType && { idArticleType: articleType }),
        };
        setDirty(false);
    }, []);

    const checkDirty = useCallback(() => {
        // Don't mark as dirty while content is being loaded
        if (isLoadingContentRef.current) {
            return false;
        }
        const currentContent = convertToRaw(editorState.getCurrentContent());
        const snap = lastSnapshotRef.current;
        return (
            snap.title !== title ||
            JSON.stringify(snap.content) !== JSON.stringify(currentContent) ||
            snap.idArticleType !== idArticleType ||
            (snap.lead || '') !== (lead || '') ||
            (snap.coverImageId || null) !== (coverImage ? coverImage.id : null)
        );
    }, [editorState, title, idArticleType, lead, coverImage]);

    return {
        idArticle,
        setIdArticle,
        title,
        setTitle,
        lead,
        setLead,
        coverImage,
        setCoverImage,
        idArticleType,
        setIdArticleType,
        visible,
        setVisible,
        editorState,
        setEditorState,
        dirty,
        setDirty,
        updateSnapshot,
        checkDirty,
        isLoadingContentRef,
        decorator,
    };
};

const useImageUpload = (onUpload, insertAtomicBlock, showSnackbar) => {
    const [dialog, setDialog] = useState({ open: false, file: null });

    const openDialog = useCallback((file) => {
        setDialog({ open: true, file });
    }, []);

    const closeDialog = useCallback(() => {
        setDialog({ open: false, file: null });
    }, []);

    const handleFileSelect = useCallback(
        (e) => {
            e.preventDefault();
            const file = e.target.files[0];
            if (file) {
                // Client-side validation before opening dialog
                const MAX_FILE_SIZE = (parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 10) * 1024 * 1024;
                const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

                if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                    showSnackbar(`Tipo de ficheiro inválido. Permitidos: ${ALLOWED_IMAGE_TYPES.join(', ')}`, 'error');
                    return;
                }

                if (file.size > MAX_FILE_SIZE) {
                    showSnackbar(`Ficheiro demasiado grande. Máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB`, 'error');
                    return;
                }

                openDialog(file);
            }
        },
        [openDialog, showSnackbar],
    );

    const handleUploadSubmit = useCallback(
        async ({ filename, alt }) => {
            const { file } = dialog;
            if (!file || !onUpload) return;

            try {
                file.options = { filename, alt };
                const imageData = await onUpload(file);

                if (imageData) {
                    insertAtomicBlock('image', {
                        src: 'images/articles/' + imageData.filename,
                        alt: imageData.alt,
                        id: imageData.id,
                    });
                    showSnackbar('Imagem carregada', 'success');
                }
            } catch (error) {
                console.error('Error uploading image:', error);
                // Show the specific error message from validation
                const errorMessage = error.message || 'Erro ao carregar a imagem';
                showSnackbar(errorMessage, 'error');
            } finally {
                closeDialog();
            }
        },
        [dialog, closeDialog, onUpload, insertAtomicBlock, showSnackbar],
    );

    return { dialog, handleFileSelect, closeDialog, handleUploadSubmit };
};

// Pick a filename to preview a hydrated cover image. The API returns the image
// with its responsive `imageFiles` (e.g. name "x-320w.webp"); prefer a mid size.
const pickCoverFilename = (coverImage) => {
    const files = coverImage && coverImage.imageFiles ? coverImage.imageFiles : [];
    if (!files.length) return coverImage && coverImage.name ? coverImage.name : '';
    const sorted = [...files].sort((a, b) => a.width - b.width);
    const mid = sorted.find((f) => f.width >= 320) || sorted[sorted.length - 1];
    return mid.name;
};

// Cover photo upload: reuses the same ImageUploadDialog + upload endpoint as body
// images, but instead of inserting an atomic block it stores the result as the
// article's cover (article metadata, kept out of the body text).
const useCoverUpload = (onUpload, setCoverImage, showSnackbar) => {
    const [dialog, setDialog] = useState({ open: false, file: null });

    const closeDialog = useCallback(() => setDialog({ open: false, file: null }), []);

    const handleFileSelect = useCallback(
        (e) => {
            e.preventDefault();
            const file = e.target.files[0];
            if (!file) return;
            const MAX_FILE_SIZE = (parseInt(process.env.REACT_APP_MAX_IMAGE_SIZE_MB) || 10) * 1024 * 1024;
            const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                showSnackbar(`Tipo de ficheiro inválido. Permitidos: ${ALLOWED_IMAGE_TYPES.join(', ')}`, 'error');
                return;
            }
            if (file.size > MAX_FILE_SIZE) {
                showSnackbar(`Ficheiro demasiado grande. Máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB`, 'error');
                return;
            }
            // Reset the input so re-picking the same file still fires onChange.
            e.target.value = '';
            setDialog({ open: true, file });
        },
        [showSnackbar],
    );

    const handleUploadSubmit = useCallback(
        async ({ filename, alt }) => {
            const { file } = dialog;
            if (!file || !onUpload) return;
            try {
                file.options = { filename, alt };
                const imageData = await onUpload(file);
                if (imageData) {
                    setCoverImage({ id: imageData.id, filename: imageData.filename, alt: imageData.alt });
                    showSnackbar('Capa definida', 'success');
                }
            } catch (error) {
                console.error('Error uploading cover image:', error);
                showSnackbar(error.message || 'Erro ao carregar a capa', 'error');
            } finally {
                closeDialog();
            }
        },
        [dialog, closeDialog, onUpload, setCoverImage, showSnackbar],
    );

    return { dialog, handleFileSelect, closeDialog, handleUploadSubmit };
};

// UI Components
const LoadingOverlay = ({ loading }) => {
    if (!loading) return null;

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                fontSize: '1.1rem',
                color: '#666',
                borderRadius: 12,
            }}
        >
            A carregar…
        </div>
    );
};

const VideoUrlDialog = ({ open, onClose, onSubmit }) => {
    const isMobile = useIsMobile();
    const [url, setUrl] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = () => {
        if (!url.trim()) {
            setError('Indique o endereço do vídeo');
            return;
        }
        onSubmit(url);
        setUrl('');
        setError('');
    };

    const handleClose = () => {
        setUrl('');
        setError('');
        onClose();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth fullScreen={isMobile}>
            <DialogTitle>Inserir vídeo do YouTube</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin='dense'
                    label='Endereço do vídeo'
                    type='url'
                    fullWidth
                    variant='outlined'
                    value={url}
                    onChange={(e) => {
                        setUrl(e.target.value);
                        setError('');
                    }}
                    onKeyPress={handleKeyPress}
                    error={Boolean(error)}
                    helperText={error || 'ex.: https://www.youtube.com/watch?v=…'}
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='inherit'>
                    Cancelar
                </Button>
                <Button onClick={handleSubmit} variant='contained'>
                    Inserir
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SnackbarAlert = ({ snackbar, onClose }) => (
    <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        ContentProps={{ sx: { display: 'flex', justifyContent: 'center' } }}
    >
        <Alert
            onClose={onClose}
            severity={snackbar.severity}
            variant='filled'
            sx={{
                width: '100%',
                maxWidth: 400,
                borderRadius: 2,
                boxShadow: 4,
            }}
        >
            {snackbar.message}
        </Alert>
    </Snackbar>
);

// Custom block renderer for images
const mediaBlockRenderer = (block) => {
    if (block.getType() === 'atomic') {
        return {
            component: Media,
            editable: false,
        };
    }
    return null;
};

// Simplified Media component (stateless) to avoid StrictMode mount/unmount side-effects
const Media = ({ block, contentState }) => {
    try {
        const entityKey = block.getEntityAt(0);
        if (!entityKey) return null;
        const entity = contentState.getEntity(entityKey);
        const type = entity.getType();
        const data = entity.getData();
        const { src, alt } = data;
        if (type === 'image') {
            const publicUrl = process.env.PUBLIC_URL || '';
            const imagepath = src.startsWith('http') ? src : `${publicUrl}/${src}`;
            return <img src={imagepath} alt={alt || 'Imagem do artigo'} style={{ maxWidth: '100%', display: 'block' }} loading='lazy' />;
        }
        if (type === 'video') {
            return (
                <iframe
                    title={alt || 'Embedded video'}
                    width='560'
                    height='315'
                    src={src}
                    frameBorder='0'
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                    allowFullScreen
                    style={{ maxWidth: '100%' }}
                    loading='lazy'
                />
            );
        }
    } catch (e) {
        console.error('Media render failed:', e);
    }
    return null;
};

// Link component for rendering links in the editor
const Link = ({ entityKey, contentState, children }) => {
    try {
        const entity = contentState.getEntity(entityKey);
        const { url } = entity.getData();
        return (
            <a
                href={url}
                target='_blank'
                rel='noopener noreferrer'
                style={{
                    color: '#1976d2',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                }}
                onClick={(e) => {
                    // Prevent navigation in edit mode - only for preview purposes
                    if (e.ctrlKey || e.metaKey) {
                        // Allow opening in new tab with Ctrl/Cmd+Click
                        return;
                    }
                    e.preventDefault();
                }}
            >
                {children}
            </a>
        );
    } catch (e) {
        console.error('Link render failed:', e);
        return children;
    }
};

// Decorator strategy to find link entities
const findLinkEntities = (contentBlock, callback, contentState) => {
    contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
    }, callback);
};

// Create decorator for links
const createDecorator = () => {
    return new CompositeDecorator([
        {
            strategy: findLinkEntities,
            component: Link,
        },
    ]);
};

// #region ARTICLE EDITOR
const ArticleEditor = ({
    onSave,
    onUpload,
    draftInterval = DEFAULT_DRAFT_INTERVAL,
    initialContent = null,
    onSaveSuccess,
    onContentChange,
    loading = false,
    newTypeId = null,
}) => {
    // Redux
    const articleTypes = useSelector(selectAllArticleTypes);

    // Refs
    const draftTimeoutRef = useRef(null);
    const editorRef = useRef(null);

    // Custom hooks
    const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
    const {
        idArticle,
        setIdArticle,
        title,
        setTitle,
        lead,
        setLead,
        coverImage,
        setCoverImage,
        idArticleType,
        setIdArticleType,
        visible,
        setVisible,
        editorState,
        setEditorState,
        dirty,
        setDirty,
        updateSnapshot,
        checkDirty,
        isLoadingContentRef,
        decorator,
    } = useArticleState(initialContent, newTypeId);

    // When the last draft autosave landed, shown next to the save button so it
    // is obvious that nothing has been lost.
    const [lastDraftSavedAt, setLastDraftSavedAt] = useState(null);

    // Computed values
    const intervalMs = useMemo(() => parseInterval(draftInterval), [draftInterval]);

    // Save the article (draft or final) with toast feedback.
    //
    // `visible` is always sent: the API rewrites it on every save, so omitting it
    // means "unpublish". `stay` keeps the editor open (used by the publish
    // toggle, where closing on save would hide the result of the action).
    const handleSave = useCallback(
        async ({ isDraft = false, nextVisible, stay = false } = {}) => {
            if (!onSave) return;
            try {
                const content = convertToRaw(editorState.getCurrentContent());
                const coverImageId = coverImage ? coverImage.id : null;
                const wantVisible = typeof nextVisible === 'boolean' ? nextVisible : visible;
                // A draft is never public — the server forces it, so say so here
                // too rather than showing a state the save will not produce.
                const payload = {
                    title,
                    lead,
                    content,
                    isDraft,
                    idArticle,
                    idArticleType,
                    coverImageId,
                    visible: isDraft ? false : wantVisible,
                };

                const result = onSave(payload);
                const resolved = result && typeof result.then === 'function' ? await result : result;
                const success = resolved !== false;

                if (success) {
                    // Update article ID if new article was created
                    if (!idArticle && resolved?.idArticle) {
                        setIdArticle(resolved.idArticle);
                    }

                    if (!isDraft) setVisible(wantVisible);

                    // Update snapshot & dirty state
                    updateSnapshot(title, content, null, null, lead, coverImageId);

                    if (isDraft) {
                        setLastDraftSavedAt(new Date());
                    } else {
                        showSnackbar(wantVisible ? 'Guardado e publicado' : 'Guardado');
                        if (!stay && onSaveSuccess) setTimeout(() => onSaveSuccess(), 600);
                    }
                } else if (!isDraft) {
                    showSnackbar('Não foi possível guardar', 'error');
                }

                return success;
            } catch (e) {
                console.error('Save failed:', e);
                if (!isDraft) {
                    showSnackbar('Erro ao guardar', 'error');
                }
                return false;
            }
        },
        [
            editorState,
            title,
            lead,
            coverImage,
            visible,
            onSave,
            onSaveSuccess,
            idArticle,
            idArticleType,
            setIdArticle,
            setVisible,
            updateSnapshot,
            showSnackbar,
        ],
    );

    // Handle editor changes
    const handleEditorChange = useCallback(
        (newEditorState) => {
            setEditorState(newEditorState);
        },
        [setEditorState],
    );

    // Load article content when initialContent changes
    const loadArticleContent = useCallback(
        (content) => {
            // Mark that we're loading content to prevent dirty flag
            isLoadingContentRef.current = true;

            const normalizedId = normalizeArticleId(content);

            if (content && normalizedId) {
                setIdArticle(normalizedId);
                setTitle(content.title || '');
                setLead(content.lead || '');
                const cover = content.coverImage
                    ? { id: content.coverImage.idImage, filename: pickCoverFilename(content.coverImage), alt: content.coverImage.alt || '' }
                    : null;
                setCoverImage(cover);
                setIdArticleType(content.idArticleType || 1);
                setVisible(Boolean(content.visible ?? content.isVisible));

                const coverId = cover ? cover.id : null;
                const rawContent = content.content || content.body;
                const parsedContent = parseArticleContent(rawContent);

                if (parsedContent) {
                    const contentState = convertFromRaw(parsedContent);
                    const newEditorState = EditorState.createWithContent(contentState, decorator);
                    setEditorState(newEditorState);
                    updateSnapshot(content.title || '', parsedContent, normalizedId, content.idArticleType || 1, content.lead || '', coverId);
                } else {
                    // Fallback to empty editor but keep title and ID
                    setEditorState(EditorState.createEmpty(decorator));
                    updateSnapshot(
                        content.title || '',
                        convertToRaw(EditorState.createEmpty().getCurrentContent()),
                        normalizedId,
                        content.idArticleType || 1,
                        content.lead || '',
                        coverId,
                    );
                }
            } else if (content === null) {
                // Reset for new article
                setIdArticle(null);
                setTitle('');
                setLead('');
                setCoverImage(null);
                setIdArticleType(newTypeId || 1);
                setVisible(false);
                setEditorState(EditorState.createEmpty(decorator));
                updateSnapshot('', convertToRaw(EditorState.createEmpty().getCurrentContent()), null, newTypeId || 1);
            }

            // Allow dirty detection after a small delay to ensure all state updates are complete
            setTimeout(() => {
                isLoadingContentRef.current = false;
            }, 100);
        },
        [
            setIdArticle,
            setTitle,
            setLead,
            setCoverImage,
            setIdArticleType,
            setVisible,
            setEditorState,
            updateSnapshot,
            isLoadingContentRef,
            decorator,
            newTypeId,
        ],
    );

    // Handle initialContent changes
    useEffect(() => {
        loadArticleContent(initialContent);
    }, [initialContent, loadArticleContent]);

    // Track content changes and notify parent
    useEffect(() => {
        if (!onContentChange) return;

        const raw = convertToRaw(editorState.getCurrentContent());
        onContentChange({ title, content: raw, idArticle, idArticleType });

        const isDirty = checkDirty();
        if (isDirty !== dirty) setDirty(isDirty);
    }, [editorState, title, idArticle, idArticleType, onContentChange, checkDirty, dirty, setDirty]);

    // Auto-save draft after inactivity.
    //
    // Only while the article is unpublished. A draft save forces is_visible
    // false server-side, so autosaving a live article would quietly take it off
    // the site mid-edit — the one thing an autosave must never do.
    useEffect(() => {
        if (!dirty || visible) {
            if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);
            return;
        }

        if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);

        draftTimeoutRef.current = setTimeout(() => {
            if (checkDirty()) {
                handleSave({ isDraft: true });
            }
        }, intervalMs);

        return () => {
            if (draftTimeoutRef.current) clearTimeout(draftTimeoutRef.current);
        };
    }, [dirty, visible, intervalMs, handleSave, checkDirty]);

    // Ctrl/Cmd+S saves, the reflex every writer already has.
    useEffect(() => {
        const onKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
                event.preventDefault();
                handleSave({ stay: true });
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [handleSave]);

    // Handle keyboard shortcuts for formatting
    const handleKeyCommand = (command, editorState) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            handleEditorChange(newState);
            return 'handled';
        }
        return 'not-handled';
    };

    // Toggle inline styles (bold, italic)
    const toggleInlineStyle = (style) => {
        handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
    };

    // Toggle block type (headings, lists, etc.)
    const toggleBlockType = (blockType) => {
        handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
    };

    // Helper to check current block type
    const getCurrentBlockType = () => {
        const selection = editorState.getSelection();
        return editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();
    };

    // Undo/Redo handlers
    const handleUndo = () => {
        handleEditorChange(EditorState.undo(editorState));
    };

    const handleRedo = () => {
        handleEditorChange(EditorState.redo(editorState));
    };

    // Check if undo/redo are available
    const canUndo = editorState.getUndoStack().size > 0;
    const canRedo = editorState.getRedoStack().size > 0;

    // Insert atomic block (image, video)
    const insertAtomicBlock = useCallback(
        (entityType, entityData) => {
            try {
                const content = editorState.getCurrentContent();
                const withEntity = content.createEntity(entityType, 'IMMUTABLE', entityData);
                const entityKey = withEntity.getLastCreatedEntityKey();
                let nextState = EditorState.set(editorState, { currentContent: withEntity });
                // Force selection collapse at end before inserting to reduce DOM churn
                nextState = EditorState.forceSelection(nextState, nextState.getSelection());
                const newState = AtomicBlockUtils.insertAtomicBlock(nextState, entityKey, ' ');
                handleEditorChange(newState);
            } catch (error) {
                console.error('Error inserting atomic block:', error);
            }
        },
        [editorState, handleEditorChange],
    );

    // Media handling
    const {
        dialog: imageUploadDialog,
        handleFileSelect: handleImageSelect,
        closeDialog: handleImageDialogClose,
        handleUploadSubmit: handleImageUploadSubmit,
    } = useImageUpload(onUpload, insertAtomicBlock, showSnackbar);

    // Cover photo (article metadata — not inserted into the body).
    const {
        dialog: coverUploadDialog,
        handleFileSelect: handleCoverSelect,
        closeDialog: handleCoverDialogClose,
        handleUploadSubmit: handleCoverUploadSubmit,
    } = useCoverUpload(onUpload, setCoverImage, showSnackbar);

    // Preview URL for the current cover (mirrors the body-image Media resolver).
    const coverPreviewSrc = coverImage
        ? coverImage.filename && coverImage.filename.startsWith('http')
            ? coverImage.filename
            : `${process.env.PUBLIC_URL || ''}/images/articles/${coverImage.filename}`
        : '';

    const [videoDialog, setVideoDialog] = useState(false);

    const handleVideoInsert = useCallback(() => {
        setVideoDialog(true);
    }, []);

    const handleVideoDialogClose = useCallback(() => {
        setVideoDialog(false);
    }, []);

    const handleVideoSubmit = useCallback(
        (url) => {
            const videoId = extractYouTubeId(url);
            if (videoId) {
                const embedUrl = `https://www.youtube.com/embed/${videoId}`;
                insertAtomicBlock('video', { src: embedUrl });
                setVideoDialog(false);
            } else {
                showSnackbar('Link do YouTube inválido.', 'error');
            }
        },
        [insertAtomicBlock, showSnackbar],
    );

    // Link dialog state and handlers
    const [linkDialog, setLinkDialog] = useState(false);

    const handleLinkInsert = useCallback(() => {
        const selection = editorState.getSelection();
        if (selection.isCollapsed()) {
            showSnackbar('Selecione texto primeiro, ou o link é inserido na posição do cursor', 'info');
        }
        setLinkDialog(true);
    }, [editorState, showSnackbar]);

    const handleLinkDialogClose = useCallback(() => {
        setLinkDialog(false);
    }, []);

    const handleLinkSubmit = useCallback(
        ({ url, text }) => {
            try {
                const contentState = editorState.getCurrentContent();
                const selection = editorState.getSelection();

                // Create link entity
                const contentWithEntity = contentState.createEntity('LINK', 'MUTABLE', { url });
                const entityKey = contentWithEntity.getLastCreatedEntityKey();

                let newEditorState;

                if (selection.isCollapsed()) {
                    // Insert link text at cursor position
                    const contentWithText = Modifier.insertText(contentWithEntity, selection, text, null, entityKey);
                    newEditorState = EditorState.push(editorState, contentWithText, 'insert-characters');
                } else {
                    // Apply link entity to selected text
                    const contentWithLink = Modifier.applyEntity(contentWithEntity, selection, entityKey);
                    newEditorState = EditorState.push(editorState, contentWithLink, 'apply-entity');
                }

                handleEditorChange(newEditorState);
                setLinkDialog(false);
                showSnackbar('Link inserido', 'success');
            } catch (error) {
                console.error('Error inserting link:', error);
                showSnackbar('Erro ao inserir o link', 'error');
            }
        },
        [editorState, handleEditorChange, showSnackbar],
    );

    // What still stands between this article and being published. The server is
    // the authority (POST /api/articles rejects visible=true without a cover);
    // showing the same rule here means the button is never a trap.
    const publishBlockers = [];
    if (!title.trim()) publishBlockers.push('título');
    if (!coverImage) publishBlockers.push('imagem de capa');
    const canPublish = publishBlockers.length === 0;

    const focusEditor = () => editorRef.current && editorRef.current.focus();

    return (
        <EditorContainer>
            <LoadingOverlay loading={loading} />

            <EditorSection>
                <TitleInput placeholder='Título do artigo' value={title} onChange={(e) => setTitle(e.target.value)} />

                {/* Type and lead sit side by side only where there is room; on a
                    phone they stack, instead of the old fixed 200px select
                    squeezing the title into a sliver. */}
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '240px 1fr' } }}>
                    <FormControl fullWidth size='small'>
                        <InputLabel id='article-type-select-label'>Tipo</InputLabel>
                        <Select
                            labelId='article-type-select-label'
                            id='article-type-select'
                            label='Tipo'
                            value={idArticleType}
                            onChange={(e) => setIdArticleType(e.target.value)}
                        >
                            {articleTypes.map((type) => (
                                <MenuItem key={type.idArticleType} value={type.idArticleType}>
                                    {type.display_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label='Lead / entrada (opcional)'
                        value={lead}
                        onChange={(e) => setLead(e.target.value)}
                        fullWidth
                        multiline
                        maxRows={3}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${lead.length}/500`}
                    />
                </Box>

                {/* Cover photo — its own card, because it is the one field that
                    decides whether the article can go live at all. */}
                <Paper variant='outlined' sx={{ p: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    {coverImage ? (
                        <Box
                            component='img'
                            src={coverPreviewSrc}
                            alt={coverImage.alt || 'Capa'}
                            sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 84,
                                height: 84,
                                borderRadius: 1.5,
                                border: '1px dashed',
                                borderColor: 'error.light',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'error.main',
                            }}
                        >
                            <FaRegImage size={26} />
                        </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 180 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Imagem de capa</Typography>
                        <Typography variant='caption' color={coverImage ? 'text.secondary' : 'error.main'} component='div'>
                            {coverImage ? 'Usada no topo do artigo e no cartão da listagem.' : 'Obrigatória para publicar.'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <ToolbarButton as='label' className='media-button' title='Imagem de capa'>
                            <FaRegImage />
                            <span className='label'>{coverImage ? 'Substituir' : 'Escolher'}</span>
                            <input type='file' accept='image/*' style={{ display: 'none' }} onChange={handleCoverSelect} />
                        </ToolbarButton>
                        {coverImage && (
                            <ToolbarButton title='Remover capa' onClick={() => setCoverImage(null)}>
                                <FaTimes />
                            </ToolbarButton>
                        )}
                    </Box>
                </Paper>

                <Toolbar>
                    <div className='button-group'>
                        <ToolbarButton title='Anular' onClick={handleUndo} disabled={!canUndo}>
                            <FaUndo />
                        </ToolbarButton>
                        <ToolbarButton title='Refazer' onClick={handleRedo} disabled={!canRedo}>
                            <FaRedo />
                        </ToolbarButton>
                    </div>

                    <div className='button-group'>
                        <ToolbarButton title='Negrito' active={editorState.getCurrentInlineStyle().has('BOLD')} onClick={() => toggleInlineStyle('BOLD')}>
                            <FaBold />
                        </ToolbarButton>
                        <ToolbarButton
                            title='Itálico'
                            active={editorState.getCurrentInlineStyle().has('ITALIC')}
                            onClick={() => toggleInlineStyle('ITALIC')}
                        >
                            <FaItalic />
                        </ToolbarButton>
                    </div>

                    <div className='button-group'>
                        <ToolbarButton title='Subtítulo (H2)' active={getCurrentBlockType() === 'header-two'} onClick={() => toggleBlockType('header-two')}>
                            <FaHeading /> 2
                        </ToolbarButton>
                        <ToolbarButton title='Subtítulo (H3)' active={getCurrentBlockType() === 'header-three'} onClick={() => toggleBlockType('header-three')}>
                            <FaHeading /> 3
                        </ToolbarButton>
                    </div>

                    <div className='button-group'>
                        <ToolbarButton as='label' className='media-button' title='Inserir imagem'>
                            <FaImage />
                            <span className='label'>Imagem</span>
                            <input type='file' accept='image/*' style={{ display: 'none' }} onChange={handleImageSelect} />
                        </ToolbarButton>

                        <ToolbarButton className='media-button' onClick={handleVideoInsert} title='Inserir vídeo do YouTube'>
                            <FaYoutube />
                            <span className='label'>Vídeo</span>
                        </ToolbarButton>

                        <ToolbarButton className='media-button' onClick={handleLinkInsert} title='Inserir link'>
                            <FaLink />
                            <span className='label'>Link</span>
                        </ToolbarButton>
                    </div>
                </Toolbar>

                <EditorWrapper onClick={focusEditor}>
                    <Editor
                        ref={editorRef}
                        editorState={editorState}
                        onChange={handleEditorChange}
                        handleKeyCommand={handleKeyCommand}
                        blockRendererFn={mediaBlockRenderer}
                        placeholder='Escreva o artigo…'
                    />
                </EditorWrapper>

                {/* Action bar. Sticky to the bottom of the viewport so "Guardar"
                    is one thumb away however long the article gets; the
                    safe-area padding keeps it above the iPhone home indicator. */}
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mt: 1,
                        px: 1.5,
                        py: 1.25,
                        pb: 'calc(10px + env(safe-area-inset-bottom))',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
                    }}
                >
                    <Chip
                        size='small'
                        label={visible ? 'Publicado' : 'Não publicado'}
                        color={visible ? 'success' : 'default'}
                        variant={visible ? 'filled' : 'outlined'}
                    />
                    {!canPublish && !visible && (
                        <Typography variant='caption' color='text.secondary'>
                            Falta: {publishBlockers.join(', ')}
                        </Typography>
                    )}
                    {lastDraftSavedAt && (
                        <Typography variant='caption' color='text.secondary'>
                            Rascunho guardado às {lastDraftSavedAt.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                    )}

                    <Box sx={{ flex: 1 }} />

                    {visible ? (
                        <Button variant='outlined' color='inherit' onClick={() => handleSave({ nextVisible: false, stay: true })}>
                            Despublicar
                        </Button>
                    ) : (
                        <Tooltip title={canPublish ? '' : `Falta: ${publishBlockers.join(', ')}`}>
                            <span>
                                <Button variant='outlined' disabled={!canPublish} onClick={() => handleSave({ nextVisible: true, stay: true })}>
                                    Publicar
                                </Button>
                            </span>
                        </Tooltip>
                    )}

                    <Button variant='contained' startIcon={<FaSave />} onClick={() => handleSave({})}>
                        Guardar
                    </Button>
                </Box>

                <ImageUploadDialog
                    open={imageUploadDialog.open}
                    onClose={handleImageDialogClose}
                    file={imageUploadDialog.file}
                    onSubmit={handleImageUploadSubmit}
                />

                <ImageUploadDialog
                    open={coverUploadDialog.open}
                    onClose={handleCoverDialogClose}
                    file={coverUploadDialog.file}
                    onSubmit={handleCoverUploadSubmit}
                />

                <VideoUrlDialog open={videoDialog} onClose={handleVideoDialogClose} onSubmit={handleVideoSubmit} />

                <LinkInsertDialog open={linkDialog} onClose={handleLinkDialogClose} onSubmit={handleLinkSubmit} />

                <SnackbarAlert snackbar={snackbar} onClose={closeSnackbar} />
            </EditorSection>
        </EditorContainer>
    );
};

export default ArticleEditor;
